import { useEffect } from 'react';

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useLogin from 'src/hooks/useLogin';
import { BadgesApiList } from 'src/stores/badges/api';
import { ClearBadgesRequest, GetBadgesResponse } from 'src/stores/badges/types';
import { client } from 'src/utils/api';

export const BADGES_QUERY_KEYS = {
  all: ['badges'],
  getBadges: (types: string[]) => [...BADGES_QUERY_KEYS.all, 'get', types],
};

const getBadgesQueryOptions = (types: string[]) =>
  queryOptions({
    queryKey: BADGES_QUERY_KEYS.getBadges(types),
    queryFn: () =>
      client.get<GetBadgesResponse>(BadgesApiList.GetBadges, {
        params: { types: types.join(',') },
      }),
  });

/**
 * 배지 조회
 * GET /api/v1/badges?types=PROFILE_VIEWED_ME,...
 *
 * 응답: { result: { badges: [{ badgeType, value }] } }
 * 접근: query.data?.data.result.badges
 */
export const useGetBadgesQuery = (types: string[]) => {
  const { isLogin } = useLogin();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isLogin) {
      queryClient.removeQueries({ queryKey: BADGES_QUERY_KEYS.all });
    }
  }, [isLogin, queryClient]);

  return useQuery({
    ...getBadgesQueryOptions(types),
    enabled: isLogin && types.length > 0,
  });
};

/**
 * 배지 해제
 * POST /api/v1/badges/clear
 * body: { badgeTypes: ["PROFILE_VIEWED_ME"] }
 *
 * 성공 후 해당 타입의 badges 쿼리를 invalidate
 */
export const useClearBadgesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClearBadgesRequest) => client.post(BadgesApiList.ClearBadges, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BADGES_QUERY_KEYS.all });
    },
  });
};
